/**
 * Roles and permissions — Step 99.
 *
 * The matrix has been DB-backed and fail-closed since Step 8 with no UI, so
 * changing who can do what meant SQL against production. This is the screen.
 *
 * Kai: *"I like the digital admin permissions matrix thing for what roles
 * access."* So the grid is theirs — resources grouped, one row each,
 * View / Edit / Admin columns — over our own nine-resource registry.
 *
 * Two things here that neither digitaladmin nor the reference has:
 *
 *  - **Edit and Admin imply View, enforced as you click.** Granting edit
 *    without view produces a role that can post to a page its own nav hides,
 *    which reads as a bug and gets reported as one.
 *  - **"View as this role"** — the impersonation control, beside the role it
 *    tests. Reading a permission matrix and believing it is how somebody
 *    ships a role that cannot open its own queue.
 */
import { useState } from 'react';
import { useUrlState } from '../../lib/urlState';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, Lock, Plus, Search, Trash2, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { apiFetch, apiSend } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import { useDirty } from '../../lib/dirty';
import { useConfirm } from '../../lib/confirm';
import { useAuth } from '../../lib/AuthContext';
import { cn } from '../../lib/utils';

interface Bits {
  view: boolean;
  edit: boolean;
  admin: boolean;
}
interface Resource {
  key: string;
  label: string;
  group: string;
  navPath: string | null;
}
interface Role {
  id: string;
  key: string;
  name: string;
  isSystem: boolean;
  hardcoded: boolean;
  ticketScope: 'all' | 'groups';
  seeUngrouped: boolean;
  agentCount: number;
  permissions: Record<string, Bits>;
}

const EMPTY: Bits = { view: false, edit: false, admin: false };

export function RolesPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { me, refresh } = useAuth() as any;
  /** Identity in the URL — the house rule, and it earns it here: "look at
   *  what Team Lead grants" is exactly the kind of thing somebody pastes into
   *  a message. */
  const [selectedId, setSelectedId] = useUrlState('role', null);
  const [draft, setDraft] = useState<{
    name: string;
    ticketScope: 'all' | 'groups';
    seeUngrouped: boolean;
    permissions: Record<string, Bits>;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Kai: "what if we have 50 groups… same with roles." A rail you can filter
  // is the same height at eight roles or eighty.
  const [q, setQ] = useUrlState('q', '');

  const loaded = useQuery({
    queryKey: qk.roles,
    queryFn: () => apiFetch<{ entries: Role[]; resources: Resource[] }>('/api/admin/roles'),
  });

  const roles = loaded.data?.entries ?? [];
  const resources = loaded.data?.resources ?? [];
  const selected = roles.find((r) => r.id === selectedId) ?? null;
  const stored = selected
    ? {
        name: selected.name,
        ticketScope: selected.ticketScope,
        seeUngrouped: selected.seeUngrouped,
        permissions: selected.permissions,
      }
    : null;
  const current = draft ?? stored;
  const dirty = !!stored && JSON.stringify(current) !== JSON.stringify(stored);
  const clearDirty = useDirty(dirty);

  const grouped = resources.reduce<Record<string, Resource[]>>((acc, r) => {
    (acc[r.group] ??= []).push(r);
    return acc;
  }, {});

  const bitsFor = (key: string): Bits => current?.permissions[key] ?? EMPTY;

  /**
   * Set one checkbox, keeping the grant internally consistent.
   *
   * Edit and Admin imply View. Enforced here rather than only on save so the
   * grid never shows a state the server would not store — a checkbox that
   * silently changes meaning on save is worse than one that will not tick.
   */
  const setBit = (key: string, bit: keyof Bits, value: boolean) => {
    if (!current) return;
    const was = current.permissions[key] ?? EMPTY;
    const next: Bits = { ...was, [bit]: value };
    if ((bit === 'edit' || bit === 'admin') && value) next.view = true;
    if (bit === 'view' && !value) {
      next.edit = false;
      next.admin = false;
    }
    setDraft({ ...current, permissions: { ...current.permissions, [key]: next } });
  };

  const save = useMutation({
    mutationFn: () => apiSend(`/api/admin/roles/${selectedId}`, 'PUT', current),
    onSuccess: (res) => {
      if (!res.ok) return setError(res.error ?? 'That could not be saved.');
      setError(null);
      setDraft(null);
      void qc.invalidateQueries({ queryKey: qk.roles });
      toast.success('Role saved.');
    },
  });

  const create = useMutation({
    mutationFn: (name: string) =>
      apiSend<{ id: string }>('/api/admin/roles', 'POST', {
        name,
        ticketScope: 'all',
        seeUngrouped: true,
        // A new role starts with NOTHING, which is the fail-closed rule the
        // whole permission model is built on. Starting from a copy of Agent
        // would be friendlier and would quietly grant more than intended.
        permissions: {},
      }),
    onSuccess: (res) => {
      if (!res.ok) return setError(res.error ?? 'That could not be created.');
      // Step 171 — clear the guard before navigating. Closing this editor is a
      // URL change, so otherwise the blocker asks about the save that just
      // succeeded, and the panel stays open behind the dialog.
      clearDirty();
      void qc.invalidateQueries({ queryKey: qk.roles });
      if (res.body?.id) setSelectedId(res.body.id);
    },
  });

  const impersonate = useMutation({
    mutationFn: (roleKey: string) => apiSend('/api/admin/roles/impersonate', 'POST', { roleKey }),
    onSuccess: async (res) => {
      if (!res.ok) return toast.error(res.error ?? 'That did not work.');
      await refresh?.();
      toast.success('Viewing the app as that role. Use the banner to stop.');
    },
  });

  if (loaded.isError) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4">
          <p className="text-sm font-medium">Roles could not be loaded.</p>
          <Button className="mt-2" size="sm" variant="outline" onClick={() => void loaded.refetch()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-lg font-semibold">Roles and permissions</h1>
      <p className="mt-0.5 max-w-2xl text-sm text-muted-foreground">
        What each role can reach. A role grants access; nothing is taken away by adding one, so somebody with
        two roles can do everything either allows.
      </p>
      {error && <p className="mt-2 text-xs text-destructive">{error}</p>}

      {loaded.isPending ? (
        <Skeleton className="mt-6 h-64 w-full" />
      ) : (
        <div className="mt-5 grid gap-6 lg:grid-cols-[18rem,1fr]">
          {/* ── The roles ─────────────────────────────────────────────── */}
          <div className="space-y-1.5">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="h-8 pl-7 text-sm"
                placeholder="Search roles…"
                value={q ?? ''}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            {roles
              .filter(
                (r) => !(q ?? '').trim() || r.name.toLowerCase().includes((q ?? '').trim().toLowerCase()),
              )
              .map((r) => (
                <button
                  key={r.id}
                  onClick={() => {
                    setSelectedId(r.id);
                    setDraft(null);
                    setError(null);
                  }}
                  className={cn(
                    'flex w-full flex-col items-start gap-0.5 rounded-md border px-3 py-2 text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    selectedId === r.id && 'border-brand bg-brand/5',
                  )}
                >
                  <span className="flex w-full items-center gap-1.5 text-sm font-medium">
                    {r.name}
                    {r.isSystem && <Lock className="h-3 w-3 shrink-0 text-muted-foreground" />}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {r.agentCount} {r.agentCount === 1 ? 'person' : 'people'}
                    {r.ticketScope === 'groups' && ' · their groups only'}
                  </span>
                </button>
              ))}
            <NewRole onCreate={(name) => create.mutate(name)} busy={create.isPending} />
          </div>

          {/* ── The matrix ────────────────────────────────────────────── */}
          <div>
            {!selected || !current ? (
              <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
                Pick a role to see what it can reach.
              </p>
            ) : (
              <div className="rounded-lg border bg-card">
                <div className="flex flex-wrap items-end gap-3 border-b p-4">
                  <div className="min-w-48 flex-1">
                    <label className="mb-1 block text-xs font-medium text-muted-foreground">Role name</label>
                    <Input
                      value={current.name}
                      disabled={selected.isSystem}
                      onChange={(e) => setDraft({ ...current, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-muted-foreground">
                      Which tickets
                    </label>
                    <Select
                      value={current.ticketScope}
                      disabled={selected.isSystem}
                      onChange={(e) =>
                        setDraft({ ...current, ticketScope: e.target.value as 'all' | 'groups' })
                      }
                    >
                      <option value="all">Every ticket in the organisation</option>
                      <option value="groups">Only tickets in their groups</option>
                    </Select>
                  </div>
                  {/* Step 99 — only meaningful for a group-scoped role, so it
                      only appears for one. Kai: "ungrouped tickets should be an
                      option." Both answers are right for a real desk; the copy
                      says what each one costs. */}
                  {current.ticketScope === 'groups' && (
                    <label className="flex cursor-pointer items-start gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 rounded border-input"
                        checked={current.seeUngrouped}
                        disabled={selected.isSystem}
                        onChange={(e) => setDraft({ ...current, seeUngrouped: e.target.checked })}
                      />
                      <span>
                        <span className="font-medium">Also see tickets in no group</span>
                        <span className="block text-xs text-muted-foreground">
                          Untriaged work. Off, and a ticket nobody has routed yet is invisible to this role.
                        </span>
                      </span>
                    </label>
                  )}
                  {/* Beside the role it tests, because that is when the
                      question arises. */}
                  {me?.canImpersonate && !selected.hardcoded && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={impersonate.isPending}
                      onClick={() => impersonate.mutate(selected.key)}
                    >
                      <Eye /> View as this role
                    </Button>
                  )}
                </div>

                {/* Said plainly rather than leaving every control mysteriously
                    disabled — the reference does the same, and showing a
                    built-in role read-only beats hiding it, because somebody
                    building a new role wants to see what Agent already grants. */}
                {selected.isSystem && (
                  <p className="border-b bg-muted/30 px-4 py-2 text-xs text-muted-foreground">
                    {selected.hardcoded
                      ? 'Platform Admin holds everything, in code rather than in these settings — it is the way back in if a permission is ever set wrong, so it cannot be edited.'
                      : 'This is a built-in role, shown so you can see what it grants. Create your own role to change anything.'}
                  </p>
                )}

                <div className="space-y-5 p-4">
                  {Object.entries(grouped).map(([group, items]) => (
                    <div key={group}>
                      <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {group}
                      </h3>
                      <div className="overflow-x-auto rounded-md border">
                        <table className="w-full min-w-[26rem] text-sm">
                          <thead className="bg-muted/40 text-xs">
                            <tr>
                              <th className="px-3 py-2 text-left font-medium">Area</th>
                              <th className="w-20 px-3 py-2 text-center font-medium">View</th>
                              <th className="w-20 px-3 py-2 text-center font-medium">Edit</th>
                              <th className="w-24 px-3 py-2 text-center font-medium">Admin</th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((res) => {
                              const bits = selected.hardcoded
                                ? { view: true, edit: true, admin: true }
                                : bitsFor(res.key);
                              const locked = selected.isSystem;
                              return (
                                <tr key={res.key} className="border-t">
                                  <td className="px-3 py-2">{res.label}</td>
                                  {(['view', 'edit', 'admin'] as const).map((bit) => (
                                    <td key={bit} className="px-3 py-2 text-center">
                                      <input
                                        type="checkbox"
                                        className="h-4 w-4 rounded border-input"
                                        checked={bits[bit]}
                                        disabled={locked}
                                        aria-label={`${res.label} — ${bit}`}
                                        onChange={(e) => setBit(res.key, bit, e.target.checked)}
                                      />
                                    </td>
                                  ))}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>

                {!selected.isSystem && (
                  <div className="flex items-center justify-end gap-3 border-t p-3">
                    {dirty && <span className="text-xs text-muted-foreground">Unsaved changes</span>}
                    <Button
                      variant="ghost"
                      className="text-destructive"
                      onClick={async () => {
                        if (
                          !(await confirm({
                            title: `Delete "${selected.name}"?`,
                            body: 'Anyone holding it loses that access. This cannot be undone.',
                            confirmLabel: 'Delete role',
                            destructive: true,
                          }))
                        )
                          return;
                        const res = await apiSend(`/api/admin/roles/${selected.id}`, 'DELETE');
                        if (!res.ok) return setError(res.error ?? 'That could not be deleted.');
                        // Deleting closes the editor, which is a navigation.
                        clearDirty();
                        setSelectedId(null);
                        void qc.invalidateQueries({ queryKey: qk.roles });
                      }}
                    >
                      <Trash2 /> Delete
                    </Button>
                    <Button variant="ghost" disabled={!dirty} onClick={() => setDraft(null)}>
                      Discard
                    </Button>
                    <Button disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
                      {save.isPending ? 'Saving…' : 'Save changes'}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function NewRole({ onCreate, busy }: { onCreate: (name: string) => void; busy: boolean }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  if (!open) {
    return (
      <Button variant="outline" className="w-full" onClick={() => setOpen(true)}>
        <Plus /> New role
      </Button>
    );
  }
  return (
    <form
      className="space-y-2 rounded-md border p-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (!name.trim()) return;
        onCreate(name.trim());
        setName('');
        setOpen(false);
      }}
    >
      <Input
        autoFocus
        placeholder="Support Co-ordinator"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={busy || !name.trim()}>
          <UserCheck /> Create
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
